import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Modal, Radio, InputNumber, Divider } from 'antd'
import { Button } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import type {
  ReconciliationRow,
  ReconcilePayload
} from 'screens/Reports/types/reconciliation'
import {
  useReconcilePaidTimeModal,
  type HourPayMode
} from './useReconcilePaidTimeModal'
import { DontPayReasonFields } from './DontPayReasonFields'
import { CustomTimeRangePickers } from './CustomTimeRangePickers'
import { Stack } from './ReconcileModalStack'


const modalDividerStyle = { margin: 0, padding: 0 } as const

type ReconcilePaidTimeModalProps = {
  open: boolean
  row: ReconciliationRow
  onClose: () => void
  onSave: (payload: ReconcilePayload) => void
  isLoading?: boolean
}

export function ReconcilePaidTimeModal({
  open,
  row,
  onClose,
  onSave,
  isLoading = false
}: ReconcilePaidTimeModalProps) {
  const { t } = useTranslation()
  const {
    isShiftRow,
    shiftPayMode,
    setShiftPayMode,
    hourPayMode,
    setHourPayMode,
    noPayReason,
    setNoPayReason,
    customStartDate,
    setCustomStartDate,
    customStartTime,
    setCustomStartTime,
    customEndDate,
    setCustomEndDate,
    customEndTime,
    setCustomEndTime,
    payAmountDisplay,
    payRateSummaryLabel,
    plannedSub,
    actualSub,
    canSave,
    handleSave,
    gbpNumberProps
  } = useReconcilePaidTimeModal(open, row, onSave)

  const hidePayRateInSummary = !isShiftRow && hourPayMode === 'custom_amount'

  return (
    <StyledModal
      open={open}
      onCancel={onClose}
      closable
      width={580}
      title={
        <Stack $gap={24}>
          <ModalTitleHeading level={3}>
            {t('RECONCILIATION_MODAL_TITLE')}
          </ModalTitleHeading>
          <Divider style={modalDividerStyle} />
        </Stack>
      }
      footer={
        <Stack $gap={16} $mt={24}>
          <RowEnd>
            <Button onClick={onClose} disabled={isLoading}>
              {t('CANCEL')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              isLoading={isLoading}
              disabled={!canSave}
            >
              {t('SAVE')}
            </Button>
          </RowEnd>
        </Stack>
      }
    >
      <Stack $gap={24}>
        <Stack $gap={8}>
          <MutedLabel>{t('RECONCILIATION_PAY_BY')}</MutedLabel>

          {isShiftRow ? (
            <RadioGroupVertical
              value={shiftPayMode}
              onChange={(e) => setShiftPayMode(e.target.value)}
            >
              <Radio value="shift_rate">
                <span>
                  <RadioPrimary>
                    {t('RECONCILIATION_PAY_TYPE_SHIFT_RATE')}
                  </RadioPrimary>
                  {row.payRate && (
                    <TextMuted $spaced>
                      {t('RECONCILIATION_SHIFT_RATE_FIXED_LABEL', {
                        amount: row.payRate.amount
                      })}
                    </TextMuted>
                  )}
                </span>
              </Radio>
              <Radio value="none" className="reconcile-radio-nested">
                <span>
                  <RadioLabelColumn>
                    <RadioPrimary $inline>
                      {t('RECONCILIATION_PAY_TYPE_DONT_PAY')}
                    </RadioPrimary>
                    {shiftPayMode === 'none' && (
                      <DontPayReasonFields
                        id="reconcile-dont-pay-reason-shift"
                        rows={3}
                        value={noPayReason}
                        onChange={setNoPayReason}
                        disabled={isLoading}
                      />
                    )}
                  </RadioLabelColumn>
                </span>
              </Radio>
            </RadioGroupVertical>
          ) : (
            <RadioGroupVertical
              value={hourPayMode}
              onChange={(e) => setHourPayMode(e.target.value as HourPayMode)}
            >
              <Radio value="planned" disabled={!row.payRate}>
                <RadioPrimary>
                  {t('RECONCILIATION_PAY_TYPE_PLANNED_TIME')}
                </RadioPrimary>
                <TextMuted $spaced>{plannedSub}</TextMuted>
              </Radio>
              <Radio value="actual" disabled={!row.payRate || !row.actualTime}>
                <RadioPrimary>
                  {t('RECONCILIATION_PAY_TYPE_ACTUAL_TIME')}
                </RadioPrimary>
                <TextMuted $spaced>{actualSub}</TextMuted>
              </Radio>
              <Radio value="none" className="reconcile-radio-nested">
                <RadioLabelColumn>
                  <RadioPrimary $inline>
                    {t('RECONCILIATION_PAY_TYPE_DONT_PAY')}
                  </RadioPrimary>
                  {hourPayMode === 'none' && (
                    <DontPayReasonFields
                      id="reconcile-dont-pay-reason-hourly"
                      rows={2}
                      value={noPayReason}
                      onChange={setNoPayReason}
                      disabled={isLoading}
                    />
                  )}
                </RadioLabelColumn>
              </Radio>
              <Radio
                value="custom_time"
                disabled={!row.payRate}
                className="reconcile-radio-nested"
              >
                <span>
                  <RadioLabelColumn>
                    <RadioPrimary $inline>
                      {t('RECONCILIATION_PAY_TYPE_CUSTOM_TIME')}
                    </RadioPrimary>
                    {hourPayMode === 'custom_time' && row.payRate && (
                      <CustomTimeRangePickers
                        customStartDate={customStartDate}
                        setCustomStartDate={setCustomStartDate}
                        customStartTime={customStartTime}
                        setCustomStartTime={setCustomStartTime}
                        customEndDate={customEndDate}
                        setCustomEndDate={setCustomEndDate}
                        customEndTime={customEndTime}
                        setCustomEndTime={setCustomEndTime}
                      />
                    )}
                  </RadioLabelColumn>
                </span>
              </Radio>
              <Radio
                value="custom_amount"
                disabled={!row.payRate}
                className="reconcile-radio-nested"
              >
                <RadioLabelColumn>
                  <RadioPrimary $inline>
                    {t('RECONCILIATION_PAY_TYPE_CUSTOM_AMOUNT')}
                  </RadioPrimary>
                  {hourPayMode === 'custom_amount' && row.payRate && (
                    <CustomAmountInline>
                      <InputNumber {...gbpNumberProps} />
                    </CustomAmountInline>
                  )}
                </RadioLabelColumn>
              </Radio>
            </RadioGroupVertical>
          )}
        </Stack>

        <PaySummaryPanel $singleColumn={hidePayRateInSummary}>
          <SummaryCol>
            <SummaryValue $role="label" level={2}>
              {t('RECONCILIATION_PAY_AMOUNT')}
            </SummaryValue>
            <SummaryValue $role="amount">
              {payAmountDisplay} {t('RECONCILIATION_GBP_SUFFIX')}
            </SummaryValue>
          </SummaryCol>
          {!hidePayRateInSummary && (
            <SummaryCol>
              <SummaryValue $role="label" level={2}>
                {t('RECONCILIATION_PAY_RATE')}
              </SummaryValue>
              <SummaryValue $role="rate">{payRateSummaryLabel}</SummaryValue>
            </SummaryCol>
          )}
        </PaySummaryPanel>
        <Divider style={modalDividerStyle} />
      </Stack>
    </StyledModal>
  )
}

const ModalTitleHeading = styled(Heading)`
  margin: 0;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const RowEnd = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
`

const StyledModal = styled(Modal)`
  .ant-modal-content {
    padding: 24px;
  }
  .ant-modal-header {
    padding: 0;
    margin: 0 0 12px;
    border-bottom: none;
  }
  .ant-modal-title {
    text-transform: none;
  }
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-footer {
    padding: 0;
    margin: 0;
    border-top: none;
  }
`

const RadioGroupVertical = styled(Radio.Group)`
  display: flex;
  flex-direction: column;
  gap: 8px;

  & .ant-radio-wrapper {
    align-items: flex-start;
    margin-inline: 0;
    margin-block: 0;
    padding-block: 0;
  }

  & .reconcile-radio-nested.ant-radio-wrapper {
    display: flex;
    align-items: flex-start;
  }

  & .reconcile-radio-nested .ant-radio {
    top: 0;
    align-self: flex-start;
    margin-top: 3px;
  }

  & .reconcile-radio-nested > span:not(.ant-radio) {
    flex: 1;
    min-width: 0;
  }
`

const RadioLabelColumn = styled.span`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`

const RadioPrimary = styled(Text).attrs({ level: 1, weight: 'regular' })<{
  $inline?: boolean
}>`
  display: ${({ $inline }) => ($inline ? 'inline-block' : 'block')};
  line-height: ${({ $inline }) => ($inline ? '22px' : 'normal')};
  color: ${({ theme }) => theme.color.neutral.color3};
`

const TextMuted = styled(Text).attrs({ level: 2, weight: 'medium' })<{
  $spaced?: boolean
}>`
  display: block;
  color: ${({ theme }) => theme.color.neutral.color4};
  ${({ $spaced }) => ($spaced ? 'margin-top: 2px;' : '')}
`

const MutedLabel = styled(Text).attrs({ level: 1, weight: 'regular' })`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const CustomAmountInline = styled.div`
  margin-top: 8px;
  width: 220px;
  min-width: 0;
`

const PaySummaryPanel = styled.div<{ $singleColumn?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: ${({ $singleColumn }) =>
    $singleColumn ? 'flex-start' : 'space-between'};
  gap: 24px;
  padding: 24px;
  background: ${({ theme }) => theme.color.neutral.color10};
  border-radius: 20px;
`

const SummaryCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`

const SummaryValue = styled(Text)<{
  $role: 'label' | 'amount' | 'rate'
}>`
  color: ${({ theme, $role }) =>
    $role === 'label'
      ? theme.color.neutral.color5
      : theme.color.neutral.color3};
  font-weight: ${({ $role }) => ($role === 'amount' ? 700 : undefined)};
`

export default ReconcilePaidTimeModal
